import * as React from 'react';
import { Pagination, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloseCircleOutlined } from '@ant-design/icons';
import Search from 'antd/es/input/Search';

interface IBrand {
    id: number;
    name: string;
}

interface IApiAccessUser {
    id: number;
    name?: string;
    username?: string;
    allowedBrands?: IBrand[];
}

interface IProps {
    user?: IApiAccessUser | null;
    loading?: boolean;
}

const ApiAccessBrandsTab: React.FC<IProps> = ({ user, loading }) => {
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [searchValue, setSearchValue] = React.useState('');

    const filteredBrands = React.useMemo(() => {
        const allowedBrands = user?.allowedBrands || [];
        if (!searchValue) {
            return allowedBrands;
        }
        const normalized = searchValue.toLowerCase();
        return allowedBrands.filter((brand) => {
            const nameMatch = brand.name?.toLowerCase().includes(normalized);
            const idMatch = `${brand.id}`.includes(normalized);
            return nameMatch || idMatch;
        });
    }, [user?.allowedBrands, searchValue]);
    const paginatedBrands = React.useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return filteredBrands.slice(startIndex, startIndex + pageSize);
    }, [filteredBrands, page, pageSize]);

    const columns: ColumnsType<any> = [
        {
            title: 'Brand ID',
            dataIndex: 'id',
            width: 140,
        },
        {
            title: 'Brand Name',
            dataIndex: 'name',
        },
        {
            title: 'Operator ID',
            dataIndex: 'operatorId',
            width: 140,
        },
        {
            title: 'Operator Name',
            dataIndex: 'operatorName',
        },
    ];

    const dataSource = paginatedBrands.map((brand) => ({
        key: brand.id,
        id: brand.id,
        name: brand.name,
        operatorId: user?.id ?? '-',
        operatorName: user?.username || user?.name || '-',
    }));

    return (
        <>
            <Search
                className="mb-4"
                placeholder="Search brands"
                value={searchValue}
                onChange={(event) => {
                    setSearchValue(event.target.value);
                    setPage(1);
                }}
                suffix={
                    <CloseCircleOutlined
                        className={`cursor-pointer text-gray-400 hover:text-gray-600 ${searchValue.length > 0 ? 'opacity-100' : 'opacity-0'}`}
                        onClick={() => {
                            setSearchValue('');
                            setPage(1);
                        }}
                    />
                }
            />
            <Table
                dataSource={dataSource}
                columns={columns}
                loading={loading}
                className="w-full pb-4 rounded-lg border border-gray-200 bg-white"
                pagination={false}
            />
            <div className="flex justify-end mt-4">
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={filteredBrands.length}
                    onChange={(nextPage, nextPageSize) => {
                        setPage(nextPage);
                        setPageSize(nextPageSize);
                    }}
                    showSizeChanger
                    pageSizeOptions={['10', '20', '50', '100']}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                />
            </div>
        </>
    );
};

export default ApiAccessBrandsTab;
